// tslint:disable:no-commented-code
import { makeRouterUsingRegisteredControllers } from '@jet/environment/routing';
import { DefaultActionProvider, Dispatcher } from './base';
import { GetStaticCanonicalUrlIntentController } from './intents/get-static-canonical-url/';
import {
    type Dependencies,
    makeDependencies,
    ObjectGraphType,
} from './dependencies';
import { LintMetricsEventIntentController } from './intents/metrics';
import type { ObjectGraph } from '@jet/environment/dependencies';
import { RouteUrlIntentController } from './intents/route-url/controller';
import { Runtime } from '@jet/environment/runtime';
import { HomePageIntentController } from './intents/home-page/HomePageController';
import { BrowsePageIntentController } from './intents/browse-page/BrowsePageController';
import { AlbumPageIntentController } from './intents/album-page/AlbumPageIntentController';
import { ArtistPageIntentController } from './intents/artist-page/ArtistPageIntentController';
import { BrowseDetailPageIntentController } from './intents/browse-page/detail/BrowseDetailPageController';
import { BrowseDetailPaginationIntentController } from './intents/browse-page/detail/paginate/BrowseDetailPaginationIntentController';
import { BrowsePlaylistsPageIntentController } from './intents/browse-page/detail/playlists/BrowsePlaylistsPageIntentController';
import { PlaylistPageIntentController } from './intents/playlist-page/PlaylistPageIntentController';
import { PaginationIntentController } from './intents/pagination/PaginationIntentController';
import { DetailPageIntentController } from './intents/detail-page/DetailPageIntentController';
import { SeeAllPageIntentController } from './intents/see-all/SeeAllPageIntentController';
import { SeeAllPaginationIntentController } from './intents/see-all/paginate/SeeAllPaginationIntentController';
import { SearchPageIntentController } from './intents/search/SearchPageIntentController';
import { QueryPlaybackIntentController } from './intents/query-playback/QueryPlaybackIntentController';
import { TrackMetadataIntentController } from './intents/track-metadata/TrackMetadataIntentController';

// UI Partial Intent Controllers
import { ContextMenuIntentController } from './intents/context-menu/ContextMenuIntentController';
import { ModifyPlaylistIntentController } from './intents/context-menu/playlists/ModifyPlaylistIntentController';
import { UserComponentsIntentController } from './intents/user-components/UserComponentsIntentController';

// Library Intent Controllers
import { LibraryListPageIntentController } from './intents/library/list/LibraryListPageIntentController';
import { LibraryPlaylistsIntentController } from './intents/library/playlists/LibraryPlaylistsIntentController';
import { LibraryPlaylistPageIntentController } from './intents/library/playlist/LibraryPlaylistPageIntentController';
import { LibraryRecentlyAddedPageIntentController } from './intents/library/recently-added/RecentlyAddedPageIntentController';
import { LibraryTracksPageIntentController } from './intents/library/tracks/LibraryTracksPageIntentController';
import { RefreshPageIntentController } from './intents/refresh-page/RefreshPageIntentController';
import { RefreshRecentlyPlayedIntentController } from './intents/home-page/recently-played/RefreshRecentlyPlayedIntentController';
import { LibraryPlaylistPaginationIntentController } from './intents/library/playlist/LibraryPlaylistPaginationIntentController';

export function bootstrap(dependencies: Dependencies): Runtime {
    // Setup dependency graph
    const dependencyObjectGraph = makeDependencies(dependencies);
    // Create dispatcher
    const dispatcher = new Dispatcher(new DefaultActionProvider());
    const baseObjectGraph = dependencyObjectGraph.adding(
        ObjectGraphType.dispatcher,
        dispatcher,
    );

    // Register special controllers
    dispatcher.register(GetStaticCanonicalUrlIntentController);
    dispatcher.register(LintMetricsEventIntentController);
    dispatcher.register(RouteUrlIntentController);

    // Pages
    dispatcher.register(HomePageIntentController);
    dispatcher.register(BrowsePageIntentController);
    dispatcher.register(PaginationIntentController);

    // Detail Pages
    dispatcher.register(AlbumPageIntentController);
    dispatcher.register(ArtistPageIntentController);
    dispatcher.register(BrowseDetailPageIntentController);
    dispatcher.register(PlaylistPageIntentController);
    dispatcher.register(SeeAllPageIntentController);
    dispatcher.register(SeeAllPaginationIntentController);
    dispatcher.register(BrowseDetailPaginationIntentController);
    dispatcher.register(BrowsePlaylistsPageIntentController);

    // Search Page
    dispatcher.register(SearchPageIntentController);

    // Library Pages
    dispatcher.register(LibraryPlaylistsIntentController);
    dispatcher.register(LibraryPlaylistPageIntentController);
    dispatcher.register(LibraryPlaylistPaginationIntentController);
    dispatcher.register(LibraryRecentlyAddedPageIntentController);
    dispatcher.register(LibraryTracksPageIntentController);

    // Common UI Intent Controllers
    dispatcher.register(ContextMenuIntentController);
    dispatcher.register(ModifyPlaylistIntentController);
    dispatcher.register(QueryPlaybackIntentController);
    dispatcher.register(TrackMetadataIntentController);
    dispatcher.register(UserComponentsIntentController);

    // Refresh Page Invalidation Intent
    dispatcher.register(RefreshPageIntentController);

    // Refresh Recently Played Invalidation Intent
    dispatcher.register(RefreshRecentlyPlayedIntentController);

    // The LibraryListPageIntentController must be registered after other
    // `/library/{segment}` controllers and before the DetailPageIntentController
    //  as it handles a catch-all URL case that shouldn't override other
    // controllers. For example, the LibraryPlaylistsIntentController handles
    // the path `/library/playlists` which could also match that of the
    // LibraryListPageIntentController, `/library/{pageType}`. The controller
    // registration order impacts path priority match.
    dispatcher.register(LibraryListPageIntentController);

    // The DetailPageIntentController must be registered after other
    // controllers as it handles a catch-all URL case that shouldn't override
    // other controllers. For example, the AlbumPageIntentController
    // handles the path `/{storefront}/album/{id}` which could also match that
    // of the DetailPageIntentController, `/{storefront}/{pageType}/{id}`.
    // The controller registration order impacts path priority match.
    dispatcher.register(DetailPageIntentController);

    // Make router after controllers are registered (or manually register
    // routes)
    const router = makeRouterUsingRegisteredControllers(
        dispatcher,
        baseObjectGraph,
    );
    const appGraph = baseObjectGraph.adding(ObjectGraphType.router, router);

    return new Runtime(dispatcher, appGraph);
}

export interface Prebootstrap {
    addIntents?(intentDispatcher: Dispatcher): void;
    modifyObjectGraph?(objectGraph: ObjectGraph): ObjectGraph;
}
